import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram, Send, Globe2, ShieldCheck, Award, Clock, Loader2, CheckCircle2 } from 'lucide-react';
import Logo from './Logo';
import CitySkyline from './CitySkyline';
import FooterSeoBand from './FooterSeoBand';
import { useLang } from '../contexts/LanguageContext';
import { useRegion } from '../contexts/RegionContext';
import { api } from '../lib/api';

const Footer = () => {
  const { t, tt } = useLang();
  const { region } = useRegion();
  const [subState, setSubState] = React.useState({ loading: false, ok: false, err: '' });
  const subscribe = async (e) => {
    e.preventDefault();
    const email = new FormData(e.target).get('email');
    setSubState({ loading: true, ok: false, err: '' });
    try {
      const res = await api.newsletter({ email, source: 'footer' });
      setSubState({ loading: false, ok: true, err: '', msg: res.message });
      e.target.reset();
      setTimeout(() => setSubState({ loading: false, ok: false, err: '' }), 5000);
    } catch (er) {
      setSubState({ loading: false, ok: false, err: er.message });
    }
  };
  return (
    <>
      {/* Pre-footer CTA strip — dark gradient, slim */}
      <section className="relative bg-gradient-to-br from-[#0e1116] via-[#191d24] to-[#0e1116] text-white overflow-hidden border-t border-gray-900/30">
        <div className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px]"></div>
        <div className="relative max-w-[1400px] mx-auto px-6 py-14 grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <div className="text-green-400 font-bold text-[12px] uppercase tracking-[0.25em] mb-2 flex items-center gap-2"><Globe2 className="w-3.5 h-3.5" /> {t('globalReach')}</div>
            <h2 className="text-3xl md:text-5xl font-black leading-tight">{tt('Engineering Without')} <span className="text-orange-500">{tt('Borders')}</span></h2>
            <p className="text-gray-300 mt-4 max-w-xl">{tt('Wherever your project is, we deliver precision engineering with local expertise and global standards.')} {t('servingWorldwide')}.</p>
          </div>
          <div className="flex flex-wrap gap-3 justify-start lg:justify-end">
            <Link to="/get-quote" className="btn-shine inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3.5 rounded uppercase text-sm tracking-wider transition-all hover:shadow-xl hover:shadow-orange-500/40">
              {t('getQuote')} <Send className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-gray-500 hover:bg-gray-600 text-white font-bold px-6 py-3.5 rounded uppercase text-sm tracking-wider transition-all">
              {t('contact')} <MessageIconAlt />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO link band: Explore All Services & Locations + All Keywords */}
      <FooterSeoBand />

      {/* Main footer — whitish background with CAD-style city skyline silhouette across the bottom */}
      <footer
        className="relative bg-[#f5f7fa] text-[#1a2030] border-t border-gray-200 overflow-hidden"
        style={{ backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #f3f6fa 60%, #e9eef5 100%)' }}
        data-testid="site-footer"
      >
        {/* Faint blueprint grid */}
        <div className="absolute inset-0 opacity-[0.5] pointer-events-none [background-image:linear-gradient(rgba(14,17,22,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(14,17,22,0.06)_1px,transparent_1px)] [background-size:40px_40px]"></div>
        {/* City skyline at the bottom edge */}
        <CitySkyline stroke="#1a2030" accent="#8a94a6" accentAlt="#f97316" />

        <div className="relative max-w-[1400px] mx-auto px-6 py-16 pb-28 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <Logo size="md" />
            <p className="text-sm leading-relaxed text-gray-600 my-6 max-w-md">
              {tt('Land Drafters is a globally integrated, multi-disciplinary engineering and CAD drafting consultancy. We collaborate with clients across')} <span className="text-green-700 font-bold">{tt('40+ countries')}</span> {tt('to design, develop, and deliver work that represents our passion for engineering.')}
            </p>

            <div className="flex flex-wrap gap-3 mb-6">
              {[
                { icon: ShieldCheck, label: 'ISO 9001:2015' },
                { icon: Award, label: '20+ Years' },
                { icon: Clock, label: '24/7 Support' }
              ].map(({ icon: Ic, label }) => (
                <div key={label} className="flex items-center gap-1.5 bg-white border border-gray-300 px-3 py-1.5 rounded text-[11px] font-bold text-green-700 uppercase tracking-wider shadow-sm">
                  <Ic className="w-3.5 h-3.5" /> {tt(label)}
                </div>
              ))}
            </div>

            <form onSubmit={subscribe} className="max-w-sm">
              <div className="flex shadow-sm">
                <input
                  required
                  type="email"
                  name="email"
                  placeholder={tt('Your email')}
                  className="flex-1 bg-white border border-gray-300 px-4 py-3 text-sm rounded-l outline-none focus:border-gray-500 text-[#1a2030]"
                />
                <button
                  disabled={subState.loading}
                  className="bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white px-4 rounded-r transition-colors"
                  aria-label={t('subscribe')}
                >
                  {subState.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              {subState.ok && <div className="mt-2 text-[11px] text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {tt(subState.msg || 'Subscribed!')}</div>}
              {subState.err && <div className="mt-2 text-[11px] text-red-600">{subState.err}</div>}
            </form>
          </div>

          <div>
            <h4 className="text-orange-600 font-bold text-sm uppercase tracking-wider mb-5">{tt('Quick Links')}</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                [t('home'), '/'],
                [t('about'), '/about'],
                [t('industries'), '/industries'],
                [tt('Coverage'), '/coverage'],
                [t('whyUs'), '/why-us'],
                [t('careers'), '/careers'],
                [tt('Blogs'), '/blogs'],
                [tt('News'), '/news'],
                [t('contact'), '/contact']
              ].map(([l, p]) => <li key={p}><Link to={p} className="link-underline text-gray-600 hover:text-green-700">{l}</Link></li>)}
            </ul>
          </div>

          <div>
            <h4 className="text-orange-600 font-bold text-sm uppercase tracking-wider mb-5">{t('services')}</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                ['Land Surveying', 'land-surveying'], ['BIM Services', 'bim-services'], ['Architectural', 'architectural'],
                ['Civil Engineering', 'civil-engineering'], ['Structural Engineering', 'structural-engineering'],
                ['MEP Engineering', 'mep-engineering'], ['Manufacturing', 'manufacturing'], ['Plant Engineering', 'plant-engineering'],
                ['GIS & Geospatial', 'gis-geospatial']
              ].map(([label, slug]) => <li key={slug}><Link to={`/services/${slug}`} className="link-underline text-gray-600 hover:text-green-700">{tt(label)}</Link></li>)}
            </ul>
          </div>

          <div>
            <h4 className="text-orange-600 font-bold text-sm uppercase tracking-wider mb-5">{tt('Get In Touch')}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2 text-gray-600"><MapPin className="w-4 h-4 mt-0.5 text-green-600 shrink-0" /> {region?.address || 'Apex, North Carolina, USA · Pune, Maharashtra, India'}</li>
              <li className="flex items-center gap-2 text-gray-600"><Phone className="w-4 h-4 text-green-600 shrink-0" /> <a href={`tel:${(region?.phone || '+17866732523').replace(/\s/g, '')}`} className="link-underline hover:text-green-700">{region?.phone || '(+1) 786-673-2523'}</a></li>
              <li className="flex items-center gap-2 text-gray-600"><Phone className="w-4 h-4 text-green-600 shrink-0" /> <a href="tel:+919960250279" className="link-underline hover:text-green-700">+91 9960 250 279</a></li>
              <li className="flex items-center gap-2 text-gray-600"><Mail className="w-4 h-4 text-green-600 shrink-0" /> <a href={`mailto:${region?.email || 'info@landdrafters.com'}`} className="link-underline hover:text-green-700">{region?.email || 'info@landdrafters.com'}</a></li>
              <li className="flex items-center gap-2 text-gray-600"><Mail className="w-4 h-4 text-green-600 shrink-0" /> <a href="mailto:landdrafters@gmail.com" className="link-underline hover:text-green-700">landdrafters@gmail.com</a></li>
              <li className="flex items-center gap-2 text-gray-500 text-xs"><Globe2 className="w-3.5 h-3.5 text-orange-500 shrink-0" /> {tt('Region')}: <span className="text-green-700 font-bold">{tt(region?.name || 'Global')}</span> · {region?.currency || 'USD'} · {region?.timezone || 'UTC'}</li>
            </ul>
            <div className="flex gap-3 mt-5">
              {[
                { Ic: Facebook,  name: 'facebook' },
                { Ic: Twitter,   name: 'twitter' },
                { Ic: Linkedin,  name: 'linkedin' },
                { Ic: Instagram, name: 'instagram' }
              ].map(({ Ic, name }) => (
                <a
                  key={name}
                  href="#"
                  className="w-9 h-9 bg-white border border-gray-300 text-gray-700 rounded flex items-center justify-center hover:bg-gray-600 hover:text-white hover:border-green-500 hover:-translate-y-1 transition-all shadow-sm"
                  aria-label={name}
                >
                  <Ic className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar — sits above the skyline */}
        <div className="relative border-t border-gray-300 bg-white/60 backdrop-blur-sm z-[1]">
          <div className="max-w-[1400px] mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-gray-600">
            <p>© {new Date().getFullYear()} Land Drafters LLC. {tt('All Rights Reserved.')}</p>
            <div className="flex gap-5">
              <Link to="/privacy-policy" className="link-underline hover:text-green-700">{tt('Privacy Policy')}</Link>
              <Link to="/terms" className="link-underline hover:text-green-700">{tt('Terms & Conditions')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

const MessageIconAlt = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>
);

export default Footer;
